import 'sosijs/dist/sosi';

// Modules
import { addLayerToList } from './list';
import { sendFileToBackend } from './request/computation';
import * as wizard from './wizard';
import {
  enrichInformationOnFeatures, keepXY, plansAndTheirBoundingAreas
} from './map/feature-tools';
import { planStyle } from './map/styles';
import { map, zoomToAll } from './map/tools';
import { warn } from './notify';
import { getNS, getSrsName, removeSymbols } from './text';

import { GeoJSON, GML, TopoJSON } from 'ol/format';
import GML32 from 'ol/format/GML32';
import { Fill, Stroke, Style } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';

// Define a SOSI format class by subclassing ol/format/GeoJSON:
class sosiFormat extends GeoJSON {
  constructor(opt_options) { super(opt_options || {}); }
  getType() { // needed, it will fail otherwise
    return 'arraybuffer';
  }
  readFeatures(source, options) {
    // console.log('readFeatures', typeof source);
    const noSymbol = removeSymbols(source); // or parser will fail
    tic('Removed symbols');
    const parser = new SOSI.Parser();
    const sosi = parser.parse(noSymbol);
    tic('Parsed sosi');
    // console.log(sosi, sosi.features.all(), sosi.dumps('geojson'));
    // console.log(
    //   sosi.features.length(), sosi.features.at(0), sosi.features.getById(1)
    // );
    tic(`${sosi.features.length()} features i sosi`);
    const geojson = sosi.dumps('geojson');
    tic(`Converted to geojson, ${geojson.features.length} features i geojson`);
    // console.log(clock(), 'Converted to geojson', sosi.dumps('geojson'));
    return super.readFeatures(geojson, options);
  }
}

const getOlFormat = (file, text) => {
  const type = getFileType(file);
  const gml = (type === 'application/gml+xml');
  const format = {
    'text/vnd.sosi': new sosiFormat(),
    // 'application/gml+xml': new GML({
    'application/gml+xml': new GML32({
      featureNS: { app: gml ? getNS(text) : undefined },
      featureType: plansAndTheirBoundingAreas.map(p => `app:${p}`),
      srsName: gml ? getSrsName(text) : undefined
    }),
    'application/json': new GeoJSON(),
    'application/geo+json': new GeoJSON(),
    'application/topo+json': new TopoJSON()
  };
  return format[type];
};

const onlyKeepSuitable = (layer) => {
  layer.getSource().getFeatures().forEach(f => {
    f.get('suitable') || layer.getSource().removeFeature(f);
  });
};

const clock = () => {
  const now = new Date();
  return `${now.toLocaleTimeString('no-NO')}.${now.getMilliseconds()}`;
};
const tic = (text) => console.log(clock(), text);

const getExtention = (file) => file.name.split('.').pop().toLowerCase();

const getFileType = (file) => {
  // file.type for sosi in chrome gives empty string
  const ext2type = {
    gml: 'application/gml+xml',
    json: 'application/json',
    geojson: 'application/geo+json',
    sos: 'text/vnd.sosi',
    topojson: 'application/topo+json'
  };
  return file.type || ext2type[getExtention(file)];
};

const loadFileLocally = (file, charset) => new Promise((resolve, reject) => {
  if (!valid(file)) return reject(`Ugyldig fil «${file.name}»`);
  const sosi = (getFileType(file) === 'text/vnd.sosi');
  tic('Start loading');
  // Read file locally:
  const reader = new FileReader();
  reader.readAsText(file, charset || 'UTF-8');
  // When file is read, onload event is triggered:
  reader.onload = () => {
    try {
      tic('Loaded');
      // console.log('File read:', reader.result.substr(0, 100));
      if (sosi && reader.result.indexOf('Ø') < 0) {
        // https://stackoverflow.com/a/5396742
        // console.log(decodeURIComponent(escape(reader.result.substr(0, 100))));
        // console.log(decodeURIComponent(escape(reader.result)));
        resolve(loadFileLocally(file, 'ISO8859-10'));
      }
      const text = (sosi && reader.result.indexOf('Ø') < 0) ?
        decodeURIComponent(escape(reader.result)) : reader.result;
      const s = new VectorSource({
        features: getOlFormat(file, text).readFeatures(text, {
          // dataProjection: 'EPSG:25832', // detected from file
          featureProjection: map.getView().getProjection()
        })
      });
      // Remove third coordinate, otherwise back-end will fail:
      s.getFeatures().forEach(f => keepXY(f.getGeometry()));
      // s.once('addfeature', () => tic('addfeature'));
      // s.once('changefeature', () => console.log('changefeature'));
      s.once('featuresloadstart', (fls) => {
        tic('Finished loading (featuresloadstart)')
        console.log(s.getFeatures().length);
      });
      // s.once('featuresloadend', () => tic('featuresloadend'));
      map.once('loadend', () => tic('Map completely visible (loadend)'));
      const fileLayer = new VectorLayer({
        properties: {
          id: file.name,
          type: 'file',
          sosi: (getFileType(file) === 'text/vnd.sosi')
        },
        source: s,
        style: planStyle
      });
      tic('Layer created');
      map.addLayer(fileLayer);
      tic('Added layer to map');
      resolve(fileLayer)
      // Assign extra information to those features which will
      // be sent to back-end for emission computations:
      enrichInformationOnFeatures(fileLayer);
      // onlyKeepSuitable(fileLayer);
      addLayerToList(fileLayer);
    } catch (e) { reject(e); }
  };
});

const inputFile = document.getElementById('upload');
const upBtn = document.getElementById('uploadBtn');

const sendFileToServer = (file) => { // whole file, without filtering features.
  // Send file to back-end:
  upBtn.classList.add('loading');
  upBtn.disabled = true;
  sendFileToBackend(file).then(promised => {
    const id = 'plannedEmissions';
    promised.id = id;
    document.getElementById(id).replaceWith(promised);
    moveTo('results');
    upBtn.classList.remove('loading');
    upBtn.disabled = false;
  }).catch(e => {
    console.log('Feil', e);
  });
};

const valid = (file) => {
  const extentions = inputFile.accept.split(',').map(p => p.slice(1));
  const allowedExtention = (extentions.indexOf(getExtention(file)) > -1);
  const notTooBig = (file.size < Math.pow(1024, 3)); // 1 GB
  const supportedFormat = getOlFormat(file);
  return (allowedExtention && notTooBig && supportedFormat);
};

// // Set up map with Drag and Drop interaction:
// map.addInteraction(new DragAndDrop({
//   formatConstructors: [SOSI, GeoJSON, TopoJSON]
// }));

const loadFiles = (files) => {
  upBtn.classList.add('loading');
  upBtn.disabled = true;
  const promises = [];
  const len = files.length;
  for (let i = 0; i < len; i++) promises.push(loadFileLocally(files[i]));
  // sendFileToServer(files[i]);
  Promise.allSettled(promises).then(results => {
    upBtn.classList.remove('loading');
    upBtn.disabled = false;
    const loaded = [];
    results.forEach(r => {
      if (r.status === 'fulfilled') loaded.push(r.value);
      else { console.log(r.reason); warn('Kunne ikke laste opp fil'); }
    });
    zoomToAll(loaded);
  });
};

export const initialize = () => {
  // File upload:
  // We could use label, which is clickable, but loosing tab capability.
  upBtn.onclick = () => inputFile.click();
  inputFile.onchange = (e) => loadFiles(inputFile.files);

  const dropZone = document.getElementById('map');
  dropZone.ondrop = event => {
    // console.log('File(s) dropped');
    // Prevent default behavior, i.e. prevent file from being opened:
    event.preventDefault();
    const dt = event.dataTransfer;
    // console.log(dt.items.length, dt.files.length);
    if (dt.items) { // Use DataTransferItemList interface to access the file(s):
      const files = [];
      for (let i = 0; i < dt.items.length; i++) {
        const item = dt.items[i];
        // If dropped items aren't files, reject them:
        if (item.kind === 'file') files.push(item.getAsFile());
      }
      loadFiles(files);
    } else { // Use DataTransfer interface to access the file(s);
      loadFiles(dt.files);
    }
  };
  dropZone.ondragover = event => {
    // console.log('File(s) in drop zone');
    // Prevent default behavior, i.e. prevent file from being opened:
    event.preventDefault();
  }
};
